import React, { useEffect, useRef, useState } from 'react'; // eslint-disable-line no-unused-vars
import { DayPicker } from 'react-day-picker';
import { usePopper } from 'react-popper';
import { isValid } from 'date-fns';
import fr from 'date-fns/locale/fr';
import nlBE from 'date-fns/locale/nl-BE';
import de from 'date-fns/locale/de';

import { InputField } from '../input-field';
import { formatDate } from '../../utils/date-utils';

export const localeOptions = [
  { dropDownValue: 'nl', locale: nlBE },
  { dropDownValue: 'fr', locale: fr },
  { dropDownValue: 'de', locale: de },
];

export const DEFAULT_LOCALE_OPTION = localeOptions[0];

// TODO: resolve ref
/**
 * A Datepicker component
 * @param {{
 *   className?: string,
 *   label?: string,
 *   name?: string,
 *   onSelect?: function,
 *   ref?: React.Ref,
 *   required?: boolean,
 *   shadowDom?: boolean,
 *   showRequiredError?: boolean,
 *   value?: any,
 *   fromYear?: number,
 *   toYear?: number,
 * }} props
 * @return {JSX.Element}
 * @constructor
 */
const DatePicker = React.forwardRef(function DatePicker (props, ref) {
  const {
    className,
    label,
    locale = DEFAULT_LOCALE_OPTION,
    name,
    onSelect = (value) => null,
    required,
    shadowDom = false,
    showRequiredError = false,
    value,
    fromYear,
    toYear,
  } = props;

  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const [popperElement, setPopperElement] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [canValidate, setCanValidate] = useState(false);

  const popperRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    if (value) {
      setInputValue(formatDate(value));
    }
  }, [value]);

  useEffect(() => {
    if (canValidate) {
      inputRef?.current?.focus();
      inputRef?.current?.blur();
      setIsPopperOpen(false);
    }
  }, [canValidate]);

  useEffect(() => {
    if (showRequiredError) {
      setCanValidate(true);
    }
  }, [showRequiredError]);

  // Handles closing of datepicker popup when clicking outside the popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popperElement && !popperElement.contains(event.target)) {
        closePopper();
      }
    };
    const handleClickOutsideShadowDom = (event) => {
      if (popperElement && !event.target.shadowRoot.activeElement) {
        closePopper();
      }
    };
    const eventToListen = shadowDom ? 'mouseup' : 'mousedown';
    const handleFn = shadowDom ? handleClickOutsideShadowDom : handleClickOutside;
    document.addEventListener(eventToListen, handleFn);
    return () => {
      document.removeEventListener(eventToListen, handleFn);
    };
  }, [popperElement]);

  const closePopper = () => {
    setCanValidate(true);
    inputRef?.current?.focus();
    inputRef?.current?.blur();
    setIsPopperOpen(false);
  };

  const popper = usePopper(popperRef.current, popperElement, {
    placement: 'bottom-start',
  });

  const handleSelect = (date) => {
    if (!date) return;

    if (onSelect) {
      onSelect(date);
    }

    setInputValue(formatDate(date));
    closePopper();
  };

  const openPopper = () => {
    setIsPopperOpen(true);
  };

  const getLocale = () => {
    const foundLocaleObj = localeOptions.find(({ dropDownValue }) => dropDownValue === locale);
    return (foundLocaleObj || DEFAULT_LOCALE_OPTION).locale;
  };

  const handleFocus = () => {
    openPopper();
  };

  const handleInputChange = (event) => {
    const date = new Date(event.target.value);
    if (isValid(date)) {
      handleSelect(date);
    }
  };

  const validateDate = (value) => {
    if (!canValidate) return;
    return required && !value ? { required: true } : null;
  };

  // We don't propagate the 'required' prop to InputField,
  // but handle it here by calling a custom validation function

  return (
    <>
      <div ref={popperRef} className={className}>
        <InputField
          key={name}
          ref={inputRef}
          name={name}
          onChange={handleInputChange}
          value={inputValue}
          label={required ? `${label} *` : label}
          className={!value ? 'text-transparent' : 'text-cmblack'}
          onFocus={handleFocus}
          onKeyDown={(e) => e.preventDefault()}
          validate={validateDate}
        />
      </div>
      {isPopperOpen && (
        <div
          tabIndex="-1"
          style={popper.styles.popper}
          className="dialog-sheet z-50 text-cmblack bg-white rounded shadow-panel"
          {...popper.attributes.popper}
          ref={setPopperElement}
          role="dialog"
        >
          <DayPicker
            fromYear={fromYear}
            toYear={toYear}
            captionLayout="dropdown"
            initialFocus={isPopperOpen}
            mode="single"
            defaultMonth={value}
            selected={value}
            onSelect={handleSelect}
            locale={getLocale()}
          />
        </div>
      )}
    </>
  );
});

export { DatePicker };
